import { differenceInMinutes } from "date-fns";

type FormatTimeOption = {
  separator?: string;
  ignoreZero?: boolean;
  capitalize?: "lowercase" | "uppercase";
};

/** Split `"HH:mm:ss"` (or `"HH:mm"`) into numeric parts. Missing parts return `NaN`. */
export function splitTimeInt(timeString: string): {
  hour: number;
  minute: number;
  second: number;
} {
  const [hour, minute, second] = timeString.split(":").map((v) => Number(v));
  return { hour: hour ?? NaN, minute: minute ?? NaN, second: second ?? NaN };
}

/**
 * Format a `"HH:mm:ss"` time string as 12-hour with AM/PM, e.g. `"12:30 PM"`.
 * Returns `"No Time"` for empty input. Result is memoized.
 */
export function formatTimeAMPM(
  timeString: string,
  options: FormatTimeOption = {
    separator: ":",
    ignoreZero: false,
    capitalize: "uppercase",
  },
): string {
  const { separator, ignoreZero, capitalize } = options;
  const keyname = `${timeString}-${JSON.stringify(options)}-${ignoreZero}-${capitalize}`;

  if (!timeString) return "No Time";
  if (formatTimeAMPM.cache[keyname]) return formatTimeAMPM.cache[keyname];

  const parts = splitTimeInt(timeString);
  const hour12 = parts.hour % 12 || 12;
  const hours = hour12 < 10 ? `${ignoreZero ? "" : "0"}${hour12}` : hour12;
  const minutes = parts.minute < 10 ? `${ignoreZero ? "" : "0"}${parts.minute}` : parts.minute;
  const ampm = parts.hour >= 12 ? "PM" : "AM";

  formatTimeAMPM.cache[keyname] = `${hours}${separator}${minutes} ${
    capitalize === "lowercase" ? ampm.toLocaleLowerCase() : ampm
  }`;
  return formatTimeAMPM.cache[keyname];
}
formatTimeAMPM.cache = {} as Record<string, string>;

/** Format a `"HH:mm:ss"` time string as zero-padded 24-hour `"HH:mm"`. */
export function formatTime24h(timeString: string): string {
  if (!timeString) return "No Time";
  const { hour, minute } = splitTimeInt(timeString);
  return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
}

/** Minutes between two `"HH:mm:ss"` time strings. Returns 0 if either is empty. */
export function getTimeDifferenceMinutes(startTime: string, endTime: string): number {
  if (!startTime || !endTime) return 0;
  return differenceInMinutes(
    new Date(`1970-01-01T${endTime}Z`),
    new Date(`1970-01-01T${startTime}Z`),
  );
}
